// utils/buildingFilter.ts
// The Byggnader filter. Every filter must match (types AND company AND the
// picked buildings), and a picked building is never dropped on its own: when
// another filter excludes it, it stays picked, shown greyed with a note, and
// counts for nothing.
import { BuildingType } from '../../../types/BuildingTypeEnums';
import { BuildingSummary } from '../../../types/protocols';

export interface BuildingOption {
    value: string;
    label: string;
    /** False for a picked building the type or company filter excludes */
    matches: boolean;
}

export interface BuildingFilterState {
    /** Buildings that can be picked: those matching the type and company filters, by name */
    options: BuildingOption[];
    /** The picked buildings, in pick order, each with whether it matches */
    selected: BuildingOption[];
    /** Picked buildings the other filters exclude */
    unmatched: BuildingOption[];
    /** Buildings are picked, but none of them matches: there is nothing to show */
    noneMatch: boolean;
}

export const buildingFilterState = (
    buildings: BuildingSummary[],
    buildingTypes: BuildingType[],
    userGroup: string | null,
    selectedKeys: string[],
): BuildingFilterState => {
    const matches = (building: BuildingSummary): boolean =>
        buildingTypes.includes(building.buildingType || BuildingType.other)
        && (!userGroup || (building.responsibleGroups ?? []).includes(userGroup));
    const toOption = (building: BuildingSummary): BuildingOption => ({
        value: building.key,
        label: building.name || building.key,
        matches: matches(building),
    });

    const options = buildings
        .filter(matches)
        .map(toOption)
        .sort((a, b) => a.label.localeCompare(b.label, 'sv'));
    const selected = selectedKeys.map(key => {
        const building = buildings.find(b => b.key === key);
        return building ? toOption(building) : { value: key, label: key, matches: false };
    });
    const unmatched = selected.filter(option => !option.matches);

    return {
        options,
        selected,
        unmatched,
        noneMatch: selected.length > 0 && unmatched.length === selected.length,
    };
};

/** 'Ön', 'Ön och Udden', 'Ön, Udden och Reservatet' */
export const joinNames = (names: string[]): string =>
    names.length <= 1 ? (names[0] ?? '') : `${names.slice(0, -1).join(', ')} och ${names[names.length - 1]}`;

/** The note under Byggnader, or null when every picked building matches */
export const unmatchedNote = (state: BuildingFilterState): string | null => {
    if (state.unmatched.length === 0) return null;
    const names = joinNames(state.unmatched.map(option => option.label));
    return `${names} matchar inte valt företag eller valda byggnadstyper och räknas inte med.`;
};

/** Shown instead of the charts and the table when no picked building matches */
export const NONE_MATCH_MESSAGE =
    'Ingen av de valda byggnaderna matchar valt företag och valda byggnadstyper. Ändra urvalet under Byggnader eller filtren.';
